import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { auth, db } from '../lib/firebase';
import { collection, query, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { TopNav } from '../components/TopNav';
import { Users, Phone, DollarSign, Activity, ShieldAlert, UserPlus, X, Edit2, Filter, Check } from 'lucide-react';
import clsx from 'clsx';

export const CommunityManagement = () => {
    const { t } = useLanguage();
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');
    const [roles, setRoles] = useState(['Player']);
    const navigate = useNavigate();

    // Form states
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [maxLimit, setMaxLimit] = useState('');

    const hostId = auth.currentUser?.uid;

    useEffect(() => {
        if (!hostId) return;
        const q = query(collection(db, `hosts/${hostId}/players`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = [];
            snapshot.forEach(d => list.push({ id: d.id, ...d.data() }));
            list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setPlayers(list);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [hostId]);

    const handleOpenModal = (player = null) => {
        if (player) {
            setEditingPlayer(player);
            setPhone(player.phone || '');
            setMaxLimit(player.maxLimit || '');
            setRoles(player.roles || [player.role] || ['Player']);
        } else {
            setEditingPlayer(null);
            setName(''); setPhone(''); setMaxLimit('');
            setRoles(['Player']);
        }
        setShowModal(true);
    };

    const handleSavePlayer = async (e) => {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!hostId || !trimmedName) return;

        try {
            // PART 4: Conflict Prevention for duplicate names
            const existingPlayer = players.find(p => p.name?.toLowerCase() === trimmedName.toLowerCase() && p.id !== editingPlayer?.id);
            if (existingPlayer) {
                const alt1 = `${trimmedName} 2`;
                const alt2 = `${trimmedName} (חדש)`;
                const choice = prompt(`השחקן ${trimmedName} כבר קיים. האם ברצונך להשתמש בשם אחר? (1: ${alt1}, 2: ${alt2}, ביטול ליציאה)`);
                if (choice === '1') setName(alt1);
                else if (choice === '2') setName(alt2);
                else return; // Cancel
                return; // Re-submit with new name
            }

            const playerData = {
                name: trimmedName,
                phone: phone.trim(),
                maxLimit: maxLimit ? parseFloat(maxLimit) : 0,
                roles: roles,
                role: roles[0] || 'Player', // Fallback for legacy
                updatedAt: new Date().toISOString(),
            };

            if (editingPlayer) {
                await updateDoc(doc(db, `hosts/${hostId}/players/${editingPlayer.id}`), playerData);
            } else {
                // PART 2: ADVANCED PLAYER SCHEMA
                const advancedData = {
                    ...playerData,
                    totalProfitLoss: 0,
                    totalBuyIns: 0,
                    lifetimeValue: 0,
                    debtLimit: maxLimit ? parseFloat(maxLimit) : 0, // default to maxLimit
                    sessionsPlayed: 0,
                    totalHoursPlayed: 0,
                    hourlyWinRate: 0,
                    lastPlayedDate: null,
                    seatPreference: { hasPreference: false, seatNumber: null },
                    mealPreference: { favoriteOrder: '', restaurantNotes: '' },
                    reliabilityScore: 100,
                    tardinessScore: 0,
                    registrationStatus: 'not_registered',
                    activeRegistrations: [],
                    createdAt: new Date().toISOString()
                };
                await addDoc(collection(db, `hosts/${hostId}/players`), advancedData);
            }
            setShowModal(false);
        } catch (err) {
            console.error('Error saving player:', err);
        }
    };

    const handleDeletePlayer = async (id) => {
        if (!hostId || !window.confirm(t('confirmDeletePlayer'))) return;
        try { await deleteDoc(doc(db, `hosts/${hostId}/players/${id}`)); }
        catch (err) { console.error('Error deleting player:', err); }
    };

    const formatCurrency = (amount) => `₪${(amount || 0).toLocaleString()}`;
    const filteredPlayers = players.filter(p => {
        const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.phone?.includes(searchQuery);
        const playerRoles = p.roles || [p.role] || [];
        const matchesRole = roleFilter === 'All' || playerRoles.includes(roleFilter);
        return matchesSearch && matchesRole;
    });

    const ROLES = ['All', 'Player', 'Dealer', 'Floor'];

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />

            <div className="flex flex-col flex-1 min-h-0 w-full max-w-xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <div>
                        <h1 className="text-[clamp(1.5rem,4vw,2rem)] font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 leading-none font-heebo">
                            {t('navCommunity')}
                        </h1>
                        <p className="text-xs text-slate-500 mt-1 font-heebo">{t('communitySubtitle')}</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs rounded-xl transition-colors font-heebo shadow-lg shadow-amber-500/20"
                    >
                        <UserPlus className="w-3.5 h-3.5" />
                        {t('addNewPlayer')}
                    </button>
                </div>

                {/* Search & Filters */}
                <div className="mb-6 flex-shrink-0 space-y-3">
                    <input
                        type="text"
                        placeholder={t('searchNamePhone')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-2xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50 font-heebo shadow-inner"
                    />
                    
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                        <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                        {ROLES.map(role => (
                            <button
                                key={role}
                                onClick={() => setRoleFilter(role)}
                                className={clsx(
                                    "px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all border whitespace-nowrap",
                                    roleFilter === role 
                                        ? "bg-amber-500 border-amber-400 text-slate-950" 
                                        : "bg-slate-900 border-white/5 text-slate-500 hover:border-white/10"
                                )}
                            >
                                {role === 'All' ? t('allGroup') : role === 'Player' ? t('rolePlayer') : role === 'Dealer' ? t('roleDealer') : t('roleFloor')}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scrollable Player List — full-width single-column sleek cards */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pb-4">
                    {loading ? (
                        [1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-900/50 rounded-xl animate-pulse" />)
                    ) : filteredPlayers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-600">
                            <Users className="w-12 h-12 mb-3 opacity-30" />
                            <p className="text-sm font-heebo">{searchQuery ? t('noPlayersFound') : t('emptyPlayersHelp')}</p>
                        </div>
                    ) : filteredPlayers.map(player => (
                        <div
                            key={player.id}
                            onClick={() => navigate(`/player/${player.id}`)}
                            className="w-full flex items-center gap-4 px-4 py-4 bg-slate-900/60 border border-white/5 rounded-2xl hover:border-amber-500/20 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all cursor-pointer"
                        >
                            {/* Avatar/Initials */}
                            <div
                                className={clsx(
                                    "flex-shrink-0 w-12 h-12 rounded-2xl border flex items-center justify-center transition-colors relative shadow-inner overflow-hidden",
                                    player.roles?.includes('Floor') || player.role === 'Floor' ? "bg-amber-500/10 border-amber-500/20 text-amber-500" :
                                    player.roles?.includes('Dealer') || player.role === 'Dealer' ? "bg-blue-500/10 border-blue-500/20 text-blue-400" :
                                    "bg-slate-800 border-white/5 text-slate-300"
                                )}
                            >
                                <span className={clsx(
                                    "text-lg font-black font-heebo uppercase",
                                    player.role === 'Floor' ? "text-amber-500" : "text-white"
                                )}>
                                    {(player.name || '?')[0].toUpperCase()}
                                </span>
                            </div>

                            {/* Info & Stats Grid */}
                            <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-2 gap-y-1">
                                <div className="col-span-2 flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="text-sm font-black text-white truncate font-heebo leading-none">{player.name}</div>
                                        <div className="flex gap-1">
                                            {(player.roles || [player.role || 'Player']).map(r => (
                                                <span key={r} className={clsx(
                                                    "text-[7px] font-black px-1.5 py-0.5 rounded-full border tracking-widest uppercase",
                                                    r === 'Floor' ? "bg-amber-500/20 border-amber-500/30 text-amber-400" :
                                                    r === 'Dealer' ? "bg-blue-500/20 border-blue-500/30 text-blue-400" :
                                                    "bg-slate-800 border-white/10 text-slate-500"
                                                )}>
                                                    {r === 'Player' ? t('rolePlayer') : r === 'Dealer' ? t('roleDealer') : t('roleFloor')}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={clsx(
                                        'text-xs font-black tabular-nums font-inter',
                                        (player.totalProfitLoss || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                    )}>
                                        {(player.totalProfitLoss || 0) >= 0 ? '+' : ''}{formatCurrency(player.totalProfitLoss)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-inter">
                                    <Activity className="w-2.5 h-2.5" /> {player.sessionsPlayed || 0} {t('sessions')}
                                </div>
                                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-inter">
                                    <DollarSign className="w-2.5 h-2.5 text-emerald-500/50" /> {formatCurrency(player.totalBuyIns)} {t('total')}
                                </div>
                                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-inter truncate">
                                    <ShieldAlert className="w-2.5 h-2.5 text-amber-500/50" /> {formatCurrency(player.debtLimit || player.maxLimit)} {t('credit')}
                                </div>
                                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-inter">
                                    <Users className="w-2.5 h-2.5 text-blue-500/50" /> {Math.round(player.reliabilityScore || 100)}% {t('reliability')}
                                </div>
                            </div>

                            {/* Edit button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(player);
                                }}
                                className="flex-shrink-0 p-2 text-slate-500 hover:text-amber-400 hover:bg-slate-800 rounded-xl transition-colors"
                            >
                                <Edit2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit / Create Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
                    <div className="w-full sm:max-w-md bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                            <h3 className="text-base font-black text-white font-heebo">
                                {editingPlayer ? t('editPlayer') : t('addNewPlayer')}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-white/5">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <form onSubmit={handleSavePlayer} className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs text-slate-400 mb-1.5 font-heebo">{t('fullName')}</label>
                                <input
                                    type="text" required value={name} onChange={e => setName(e.target.value)}
                                    placeholder={t('fullNamePlaceholder')}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500 font-heebo"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1.5 font-heebo">{t('phoneNumber')}</label>
                                <input
                                    type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                                    placeholder={t('phonePlaceholder')}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500 font-inter"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1.5 font-heebo">{t('maxBuyinLimit')}</label>
                                <input
                                    type="number" min="0" step="100" value={maxLimit} onChange={e => setMaxLimit(e.target.value)}
                                    placeholder={t('maxLimitPlaceholder')}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500 tabular-nums"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1.5 font-heebo">{t('designations')}</label>
                                <div className="flex gap-2">
                                    {['Player', 'Dealer', 'Floor'].map(r => (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => {
                                                if (roles.includes(r)) {
                                                    if (roles.length > 1) setRoles(prev => prev.filter(x => x !== r));
                                                } else {
                                                    setRoles(prev => [...prev, r]);
                                                }
                                            }}
                                            className={clsx(
                                                "flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border",
                                                roles.includes(r)
                                                    ? "bg-amber-500 border-amber-400 text-slate-950"
                                                    : "bg-slate-800 border-slate-700 text-slate-500"
                                            )}
                                        >
                                            {r === 'Player' ? t('rolePlayer') : r === 'Dealer' ? t('roleDealer') : t('roleFloor')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3 pt-1">
                                <button type="submit" className="flex-1 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-xl transition-colors font-heebo">
                                    {t('savePlayer')}
                                </button>
                                {editingPlayer && (
                                    <button
                                        type="button" onClick={() => handleDeletePlayer(editingPlayer.id)}
                                        className="py-3 px-4 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-bold rounded-xl border border-rose-500/20 transition-colors font-heebo"
                                    >
                                        {t('delete')}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
